import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../config';
import Pagination from './pagination';
import { Badge } from './badge';
import DealRecipientCompany from './dealRecipientCompany';
import {
  getParticipationBadgeVariant,
  getParticipationLabel,
  useDealCardCollection,
} from '../../hooks/deal-card';

function mergeContentCache(items, contentCache) {
  return (Array.isArray(items) ? items : []).map((item) => {
    const cachedContent = contentCache[item?._id];
    return cachedContent
      ? { ...item, press_release_content_original_format: cachedContent }
      : item;
  });
}

function renderArticleBody(item) {
  const detailParagraphs = Array.isArray(item.detailParagraphs)
    ? item.detailParagraphs
    : [];

  if (!item.summary && detailParagraphs.length === 0) {
    return null;
  }

  return (
    <>
      {item.summary && <p className="text-sm mb-4">{item.summary}</p>}
      {detailParagraphs.map((paragraph, index) => (
        <p
          key={`${item.id}-paragraph-${index}`}
          className="text-sm mb-4"
          dangerouslySetInnerHTML={{ __html: paragraph }}
        />
      ))}
    </>
  );
}

export default function DealInvestorProfileList({
  surfaceKey,
  totalDeals = 0,
  visibleCount,
  getUnifiedPage,
  listVersion = 0,
  initialized = false,
  ssrFirstPage = null,
  desktopAction = null,
  mobileAction = null,
}) {
  const { t } = useTranslation();
  const [pageItems, setPageItems] = useState(ssrFirstPage?.items || []);
  const [currentPage, setCurrentPage] = useState(ssrFirstPage?.currentPage || 1);
  const [totalPages, setTotalPages] = useState(ssrFirstPage?.totalPages || 1);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(!ssrFirstPage);
  const [contentCache, setContentCache] = useState({});

  const loadPage = useCallback(async (page) => {
    setLoading(true);
    try {
      const nextPage = await getUnifiedPage(page);
      setPageItems(nextPage.items || []);
      setTotalPages(nextPage.totalPages || 1);
      setCurrentPage(nextPage.currentPage || 1);
    } finally {
      setLoading(false);
    }
  }, [getUnifiedPage]);

  useEffect(() => {
    if (!initialized || visibleCount === null || visibleCount === undefined) return;

    setExpanded({});

    if (ssrFirstPage?.items?.length) {
      setPageItems(ssrFirstPage.items);
      setTotalPages(ssrFirstPage.totalPages || 1);
      setCurrentPage(ssrFirstPage.currentPage || 1);
      setLoading(false);
      return;
    }

    loadPage(1);
  }, [
    initialized,
    listVersion,
    loadPage,
    ssrFirstPage,
    totalDeals,
    visibleCount,
  ]);

  useEffect(() => {
    if (!Object.keys(contentCache).length) return;
    setPageItems((prev) => mergeContentCache(prev, contentCache));
  }, [contentCache]);

  const { items, surfaceConfig } = useDealCardCollection({
    deals: pageItems,
    surfaceKey,
  });

  const moreText = t('investorCard.readMore', { defaultValue: 'Read more' });
  const lessText = t('investorCard.showLess', { defaultValue: 'Read less' });

  const toggleArticle = async (item) => {
    const dealId = item.id;
    const willOpen = !expanded[dealId];

    setExpanded((prev) => ({ ...prev, [dealId]: willOpen }));

    if (!willOpen || !surfaceConfig.article?.lazyLoad) return;
    if (item.redacted || !item.canViewContent || item.hasArticle) return;

    try {
      const { data } = await axios.get(`${API_URL}/deals/${dealId}/content`, {
        withCredentials: true,
      });
      const body = data?.press_release_content_original_format || '';
      if (!body) return;

      setContentCache((prev) => ({ ...prev, [dealId]: body }));
      setPageItems((prev) =>
        prev.map((entry) =>
          entry?._id === dealId
            ? { ...entry, press_release_content_original_format: body }
            : entry
        )
      );
    } catch (error) {
      // For unavailable content we keep the card collapsed without surfacing an error.
    }
  };

  if (!initialized || loading) {
    return <div />;
  }

  return (
    <div className="rounded-md">
      <div className="mb-4">
        <div className="hidden lg:flex justify-between items-end">
          <h3 className="text-lg lg:text-2xl text-stone-900 font-semibold lg:text-left flex items-end">
            {t('pageInvProf.lastInvestments')}
            <span className="bg-clr6 px-2 lg:px-4 rounded-md text-sm lg:text-base text-clr2 ml-2 lg:ml-4">
              {t('pageInvProf.visibleDealsInfo', { visible: visibleCount, total: totalDeals })}
            </span>
          </h3>
          {desktopAction}
        </div>

        <div className="lg:hidden flex flex-col items-center text-center px-4">
          <h3 className="text-base font-semibold text-stone-900">
            {t('pageInvProf.lastInvestments')}
          </h3>
          <div className="text-sm text-clr8 mt-1">
            {t('pageInvProf.visibleDealsInfo', { visible: visibleCount, total: totalDeals })}
          </div>
          {mobileAction}
        </div>
      </div>

      <div className="border-t border-black">
        {items.map((item) => {
          const typeLabel = getParticipationLabel(item.participationType, t);
          const badgeVariant = getParticipationBadgeVariant(item.participationType);
          const showArticleToggle =
            !item.redacted &&
            item.canViewContent &&
            (item.hasArticle || surfaceConfig.article?.lazyLoad);
          const articleBody = renderArticleBody(item);

          return (
            <div key={item.id} className="py-2 lg:py-4 border-b border-black">
              <div className="lg:hidden flex justify-between items-center text-xs text-clr8">
                <span>{item.announcementDate || '—'}</span>
                {item.redacted ? (
                  <span className="inline-block min-w-[72px] h-4 rounded bg-gray-200 filter blur-[2px] select-none">
                    &nbsp;
                  </span>
                ) : (
                  <span>{item.amount || '—'}</span>
                )}
                <Badge text={typeLabel} variant={badgeVariant} />
              </div>

              <div className="hidden lg:flex items-start space-x-4">
                <div className="w-1/6 text-sm text-clr8">
                  {item.announcementDate || '—'}
                </div>
                <div className="w-1/6">
                  {item.redacted ? (
                    <div className="h-4 rounded bg-gray-200 filter blur-[2px] select-none" />
                  ) : (
                    <div className="text-sm text-clr8">{item.amount || '—'}</div>
                  )}
                </div>
                <div className="w-3/6 text-base font-medium">
                  {item.redacted ? (
                    <>
                      <div className="h-4 mb-2 rounded bg-gray-200 filter blur-[2px] select-none" />
                      <div className="h-4 w-2/3 rounded bg-gray-200 filter blur-[2px] select-none" />
                    </>
                  ) : (
                    <>
                      {item.title}
                      {(item.hasRecipientCompanyInfo || item.recipientCompany || item.recipientCompanyUrl) && (
                        <div className="mt-2">
                          <DealRecipientCompany
                            companyName={item.recipientCompany}
                            companyUrl={item.recipientCompanyUrl}
                            hasInfo={item.hasRecipientCompanyInfo}
                            accessMode={surfaceConfig.recipientCompany.accessMode}
                          />
                        </div>
                      )}
                      {showArticleToggle && (
                        <div>
                          <button
                            onClick={() => toggleArticle(item)}
                            className="cursor-pointer text-sm text-blue-500 underline mt-2"
                          >
                            {expanded[item.id] ? lessText : moreText}
                          </button>
                          {expanded[item.id] && (
                            <div className="text-gray-800 mt-2 max-h-[500px] overflow-auto">
                              {articleBody}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="w-1/6 flex justify-end">
                  <Badge text={typeLabel} variant={badgeVariant} />
                </div>
              </div>

              {!item.redacted && (
                <div className="lg:hidden mt-2">
                  <div className="text-base font-medium">{item.title}</div>
                  {(item.hasRecipientCompanyInfo || item.recipientCompany || item.recipientCompanyUrl) && (
                    <div className="mt-2">
                      <DealRecipientCompany
                        companyName={item.recipientCompany}
                        companyUrl={item.recipientCompanyUrl}
                        hasInfo={item.hasRecipientCompanyInfo}
                        accessMode={surfaceConfig.recipientCompany.accessMode}
                      />
                    </div>
                  )}
                  {showArticleToggle && (
                    <>
                      <button
                        onClick={() => toggleArticle(item)}
                        className="text-sm text-blue-500 underline mt-2"
                      >
                        {expanded[item.id] ? lessText : moreText}
                      </button>
                      {expanded[item.id] && (
                        <div className="text-gray-800 mt-2 max-h-[500px] overflow-auto">
                          {articleBody}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => loadPage(page)}
            useUrl={false}
          />
        </div>
      )}
    </div>
  );
}
